import { useCallback, useEffect, useRef, useState, type FocusEvent } from 'react';
import { createPortal } from 'react-dom';
import toast from 'react-hot-toast';
import {
  Camera,
  Image as ImageIcon,
  Loader2,
  RotateCcw,
  ScanLine,
  Sparkles,
  Upload,
  UserCheck,
  UserPlus,
  X,
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import {
  checkDuplicateCustomer,
  createCustomer,
  type Customer,
} from '../../lib/customers';
import { createLeadForCustomer, emitLeadCreated } from '../../lib/crm/createLead';
import { normalizePhoneNumber } from '../../services/secondaryContactLists';
import { useKeyboardBottomInset } from '../../hooks/useKeyboardBottomInset';

interface BusinessCardScanModalProps {
  isOpen: boolean;
  businessId: string;
  onClose: () => void;
  /** Fired after the lead is created so the caller can refresh its board. */
  onCreated?: (opportunityId: number, customerId: string) => void;
}

interface CardFields {
  firstName: string;
  lastName: string;
  company: string;
  jobTitle: string;
  phone: string;
  email: string;
  website: string;
  address: string;
  notes: string;
}

const EMPTY_FIELDS: CardFields = {
  firstName: '',
  lastName: '',
  company: '',
  jobTitle: '',
  phone: '',
  email: '',
  website: '',
  address: '',
  notes: '',
};

type Step = 'capture' | 'processing' | 'review' | 'creating';

interface PipelineOption {
  id: number;
  pipeline_name: string;
  stages: Array<{ id: number; name: string }>;
}

async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve((reader.result as string).split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function BusinessCardScanModal({
  isOpen,
  businessId,
  onClose,
  onCreated,
}: BusinessCardScanModalProps) {
  const [step, setStep] = useState<Step>('capture');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  // The native keyboard doesn't resize the webview (Keyboard.resize = 'none'),
  // so we track its height in --keyboard-inset and pad/shrink the panel with
  // it — otherwise the keyboard covers the bottom fields with no scroll room.
  useKeyboardBottomInset();

  /**
   * iOS doesn't auto-scroll our inner scroll container to reveal the focused
   * field (the webview never resizes), so do it ourselves once the keyboard
   * animation and inset padding have settled.
   */
  const handleFieldFocus = (e: FocusEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (
      !(target instanceof HTMLInputElement) &&
      !(target instanceof HTMLTextAreaElement) &&
      !(target instanceof HTMLSelectElement)
    ) {
      return;
    }
    window.setTimeout(() => {
      target.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, 300);
  };
  const [fields, setFields] = useState<CardFields>(EMPTY_FIELDS);
  const [matchedCustomer, setMatchedCustomer] = useState<Customer | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Lead fields are derived live from the customer info until the user edits
  // them directly — then their override wins.
  const [leadTitleOverride, setLeadTitleOverride] = useState<string | null>(null);
  const [leadInfoOverride, setLeadInfoOverride] = useState<string | null>(null);
  const [leadSource, setLeadSource] = useState('Business Card');
  /** From CRM settings: fixed source applied to every card scan (when enabled). */
  const [defaultLeadSource, setDefaultLeadSource] = useState('Business Card');

  // Pipeline/stage the lead lands in — defaults to the first pipeline's first
  // stage (the previous hardcoded behavior) but is adjustable in the review step.
  const [pipelines, setPipelines] = useState<PipelineOption[]>([]);
  const [selectedPipelineId, setSelectedPipelineId] = useState<number | null>(null);
  const [selectedStageId, setSelectedStageId] = useState<number | null>(null);

  // Load pipelines each time the modal opens (same ordering as the CRM page).
  useEffect(() => {
    if (!isOpen || !businessId || !supabase) return;
    let cancelled = false;
    (async () => {
      try {
        const { data, error: pipelinesError } = await supabase
          .from('pipelines')
          .select('id, pipeline_name, stages')
          .eq('business_id', businessId)
          .order('sort_order', { ascending: true })
          .order('pipeline_name', { ascending: true });
        if (pipelinesError) throw pipelinesError;
        if (cancelled) return;
        const list: PipelineOption[] = (data || []).map((p: any) => ({
          id: p.id,
          pipeline_name: p.pipeline_name || 'Untitled Pipeline',
          stages: Array.isArray(p.stages) ? p.stages : [],
        }));
        setPipelines(list);
        setSelectedPipelineId(list[0]?.id ?? null);
        setSelectedStageId(list[0]?.stages[0]?.id ?? null);
      } catch (err) {
        console.error('Error loading pipelines for card scan:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, businessId]);

  // Load the configured default source each time the modal opens.
  useEffect(() => {
    if (!isOpen || !businessId || !supabase) return;
    let cancelled = false;
    (async () => {
      try {
        const { data: enabled } = await supabase.rpc('get_app_setting', {
          p_business_id: businessId,
          p_app_slug: 'crm',
          p_setting_key: 'business_card_default_source_enabled',
          p_default_value: false,
        });
        let source = 'Business Card';
        if (enabled) {
          const { data: value } = await supabase.rpc('get_app_setting', {
            p_business_id: businessId,
            p_app_slug: 'crm',
            p_setting_key: 'business_card_default_source',
            p_default_value: '',
          });
          if (typeof value === 'string' && value.trim()) source = value.trim();
        }
        if (!cancelled) {
          setDefaultLeadSource(source);
          setLeadSource(source);
        }
      } catch (err) {
        console.error('Error loading default lead source:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, businessId]);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const uploadInputRef = useRef<HTMLInputElement>(null);

  // Mirrors the receipt capture UX: mobile gets a full-screen live viewfinder
  // that opens instantly, with a photo-library button in the corner.
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < 640,
  );
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const liveStreamRef = useRef<MediaStream | null>(null);
  const [isCameraStarting, setIsCameraStarting] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('environment');
  const [isShutterFlashing, setIsShutterFlashing] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const shouldShowLiveCamera = isOpen && isMobile && step === 'capture';

  const stopLiveCamera = useCallback(() => {
    const stream = liveStreamRef.current;
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      liveStreamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  const startLiveCamera = useCallback(async () => {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      setCameraError('Camera is not available in this browser');
      return;
    }
    setIsCameraStarting(true);
    setCameraError(null);
    try {
      stopLiveCamera();
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode,
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      });
      liveStreamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        try {
          await videoRef.current.play();
        } catch {
          // Some browsers reject play() before user gesture; the stream is
          // still active and will render once the user interacts.
        }
      }
    } catch (err) {
      console.error('Camera error:', err);
      const message =
        err instanceof Error && err.name === 'NotAllowedError'
          ? 'Camera permission denied. Enable camera access in Settings to scan cards.'
          : 'Could not access the camera on this device.';
      setCameraError(message);
    } finally {
      setIsCameraStarting(false);
    }
  }, [facingMode, stopLiveCamera]);

  useEffect(() => {
    if (shouldShowLiveCamera) {
      void startLiveCamera();
    } else {
      stopLiveCamera();
    }
    return () => {
      stopLiveCamera();
    };
  }, [shouldShowLiveCamera, startLiveCamera, stopLiveCamera]);

  useEffect(() => {
    if (!isOpen) {
      setStep('capture');
      setFields(EMPTY_FIELDS);
      setMatchedCustomer(null);
      setError(null);
      setCameraError(null);
      setLeadTitleOverride(null);
      setLeadInfoOverride(null);
      setLeadSource('Business Card');
      setPreviewUrl((url) => {
        if (url) URL.revokeObjectURL(url);
        return null;
      });
    }
  }, [isOpen]);

  const setField = (key: keyof CardFields, value: string) =>
    setFields((f) => ({ ...f, [key]: value }));

  /** Lead title as it will be entered in the CRM (unless overridden). */
  const computeLeadTitle = (f: CardFields): string => {
    const personName = `${f.firstName.trim()} ${f.lastName.trim()}`.trim();
    return f.company.trim() && f.company.trim() !== personName
      ? `${personName} — ${f.company.trim()}`
      : personName;
  };

  /** Lead info block as it will be entered in the CRM (unless overridden). */
  const computeLeadInfo = (f: CardFields): string =>
    ([
      'Created from a scanned business card.',
      f.jobTitle && `Title: ${f.jobTitle}`,
      f.company && `Company: ${f.company}`,
      f.website && `Website: ${f.website}`,
      f.notes,
    ].filter(Boolean) as string[]).join('\n');

  const effectiveLeadTitle = leadTitleOverride ?? computeLeadTitle(fields);
  const effectiveLeadInfo = leadInfoOverride ?? computeLeadInfo(fields);

  const findMatch = async (email: string, phone: string): Promise<Customer | null> => {
    const cleanEmail = email.trim() || null;
    const cleanPhone = phone.trim() ? normalizePhoneNumber(phone.trim()) : null;
    if (!cleanEmail && !cleanPhone) return null;
    try {
      const { matchingCustomer } = await checkDuplicateCustomer(
        businessId,
        cleanEmail,
        cleanPhone,
      );
      return (matchingCustomer as Customer) ?? null;
    } catch (err) {
      console.error('Business card customer match failed:', err);
      return null;
    }
  };

  const handleFileSelected = async (file: File | null) => {
    if (!file) return;
    setError(null);
    stopLiveCamera();
    setPreviewUrl((url) => {
      if (url) URL.revokeObjectURL(url);
      return URL.createObjectURL(file);
    });
    setStep('processing');

    try {
      const imageBase64 = await fileToBase64(file);
      const { data, error: fnError } = await supabase.functions.invoke(
        'ai-scan-business-card',
        {
          body: { businessId, imageBase64, imageMimeType: file.type || 'image/jpeg' },
        },
      );
      if (fnError) throw fnError;
      if (data?.insufficientTokens) {
        throw new Error(data.message || 'Insufficient AI tokens to scan this card.');
      }
      if (!data?.success || !data?.data) {
        throw new Error(data?.error || 'Could not read the business card.');
      }

      const d = data.data as Record<string, string | null>;
      const next: CardFields = {
        firstName: d.firstName || '',
        lastName: d.lastName || '',
        company: d.company || '',
        jobTitle: d.jobTitle || '',
        phone: d.phone || '',
        email: d.email || '',
        website: d.website || '',
        address: d.address || '',
        notes: d.notes || '',
      };
      // Company-only card: use the company as the customer name so we
      // never create a nameless customer.
      if (!next.firstName && next.company) next.firstName = next.company;
      setFields(next);
      setLeadTitleOverride(null);
      setLeadInfoOverride(null);
      setLeadSource(defaultLeadSource);
      setMatchedCustomer(await findMatch(next.email, next.phone));
      setStep('review');
    } catch (err) {
      console.error('Business card scan failed:', err);
      const message = err instanceof Error ? err.message : 'Failed to scan the card.';
      setStep('capture');
      if (isMobile) {
        // The live camera view has no inline error area — surface via toast.
        toast.error(message);
      } else {
        setError(message);
      }
    }
  };

  /** Capture the current viewfinder frame as a JPEG File and feed it into the scan pipeline. */
  const handleSnapPhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || !video.videoWidth || !video.videoHeight) {
      toast.error('Camera is not ready yet');
      return;
    }
    setIsShutterFlashing(true);
    setTimeout(() => setIsShutterFlashing(false), 180);

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob(
      (blob) => {
        if (!blob) {
          toast.error('Could not capture photo');
          return;
        }
        const file = new File([blob], `business-card-${Date.now()}.jpg`, {
          type: 'image/jpeg',
        });
        void handleFileSelected(file);
      },
      'image/jpeg',
      0.92,
    );
  };

  const handleCreateLead = async () => {
    if (!fields.firstName.trim() && !fields.lastName.trim()) {
      setError('A first or last name is required.');
      return;
    }
    setError(null);
    setStep('creating');

    try {
      // Re-check the match against the (possibly edited) contact details.
      const match = await findMatch(fields.email, fields.phone);
      setMatchedCustomer(match);

      let customer: Pick<Customer, 'id' | 'first_name' | 'last_name'>;
      if (match) {
        customer = match;
      } else {
        const noteLines = [
          fields.jobTitle && `Title: ${fields.jobTitle}`,
          fields.company && `Company: ${fields.company}`,
          fields.website && `Website: ${fields.website}`,
          fields.notes,
        ].filter(Boolean) as string[];

        customer = await createCustomer({
          business_id: businessId,
          type: 'person',
          first_name: fields.firstName.trim(),
          last_name: fields.lastName.trim(),
          email: fields.email.trim().toLowerCase() || null,
          phone: fields.phone.trim() ? normalizePhoneNumber(fields.phone.trim()) : null,
          address: fields.address.trim() || null,
          status: 'active',
          notes: noteLines.length ? noteLines.join('\n') : null,
          amount_spent: 0,
          credit_balance: 0,
          tags: [],
          preferences: {},
          tax_exempt: false,
          workflow_exempt: false,
          carfax_exempt: false,
          receive_reminders: true,
          receive_offers: true,
          is_supplier: false,
          lifetime_value: 0,
          source: 'Business Card',
          last_interaction: null,
          next_service_date: null,
          referral_source: null,
          repeat: false,
          lat: null,
          lng: null,
        } as never);
      }

      const personName =
        `${fields.firstName.trim()} ${fields.lastName.trim()}`.trim();

      const opportunityId = await createLeadForCustomer(businessId, customer, {
        source: leadSource.trim() || 'Business Card',
        title: effectiveLeadTitle.trim() || personName,
        lead_info: effectiveLeadInfo.trim() || null,
        pipeline_id: selectedPipelineId,
        stage_id: selectedStageId,
      });

      emitLeadCreated(customer.id);
      toast.success(
        match
          ? `Lead created for existing customer ${match.first_name} ${match.last_name}`.trim()
          : `Lead and new customer created for ${personName}`,
      );
      onCreated?.(opportunityId, customer.id);
      onClose();
    } catch (err) {
      console.error('Create lead from business card failed:', err);
      const message = err instanceof Error ? err.message : 'Failed to create the lead.';
      setError(
        message === 'no-pipeline' || message === 'no-stage'
          ? 'Set up a CRM pipeline with at least one stage first.'
          : message,
      );
      setStep('review');
    }
  };

  if (!isOpen) return null;

  const inputClass =
    'w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-400 transition-colors';
  const labelClass = 'block text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1';

  // Mobile capture: full-screen live viewfinder (matches the receipt scanner).
  const liveCameraContent = (
    <div
      className="fixed inset-0 bg-black"
      style={{
        zIndex: 100065,
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Hidden file input for photo library picker (no capture attr) */}
      <input
        ref={uploadInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          void handleFileSelected(e.target.files?.[0] ?? null);
          e.target.value = '';
        }}
      />

      {cameraError ? (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 text-white">
          <Camera className="h-12 w-12 mb-4 text-white/70" />
          <p className="text-base font-semibold mb-1">Camera unavailable</p>
          <p className="text-sm text-white/70 mb-6 max-w-xs">{cameraError}</p>
          <div className="flex flex-col gap-2 w-full max-w-xs">
            <button
              type="button"
              onClick={() => void startLiveCamera()}
              className="px-4 py-3 bg-white text-black rounded-xl font-semibold text-sm active:scale-[0.98] transition-transform"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              Try again
            </button>
            <button
              type="button"
              onClick={() => uploadInputRef.current?.click()}
              className="px-4 py-3 bg-white/15 text-white rounded-xl font-medium text-sm active:scale-[0.98] transition-transform"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              Choose from photos
            </button>
          </div>
        </div>
      ) : (
        <video
          ref={videoRef}
          className="absolute inset-0 w-full h-full object-cover"
          autoPlay
          playsInline
          muted
        />
      )}

      {/* Off-screen canvas for capture */}
      <canvas ref={canvasRef} className="hidden" />

      {/* Shutter flash overlay */}
      <div
        className="pointer-events-none absolute inset-0 bg-white transition-opacity duration-150"
        style={{ opacity: isShutterFlashing ? 0.9 : 0 }}
      />

      {/* Top bar */}
      <div
        className="absolute top-0 left-0 right-0 flex items-center justify-between px-4 py-3"
        style={{
          paddingTop: 'calc(env(safe-area-inset-top, 0px) + 12px)',
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.55), rgba(0,0,0,0))',
        }}
      >
        <button
          type="button"
          onClick={() => {
            stopLiveCamera();
            onClose();
          }}
          className="h-10 w-10 flex items-center justify-center rounded-full bg-black/40 text-white active:scale-[0.95] transition-transform"
          aria-label="Close"
          style={{ WebkitTapHighlightColor: 'transparent' }}
        >
          <X className="h-5 w-5" />
        </button>

        <div className="flex items-center gap-2 text-white">
          <ScanLine className="h-4 w-4 opacity-80" />
          <span className="text-sm font-semibold tracking-tight">Scan Business Card</span>
        </div>

        <button
          type="button"
          onClick={() => setFacingMode((prev) => (prev === 'environment' ? 'user' : 'environment'))}
          disabled={isCameraStarting || !!cameraError}
          className="h-10 w-10 flex items-center justify-center rounded-full bg-black/40 text-white active:scale-[0.95] transition-transform disabled:opacity-40"
          aria-label="Flip camera"
          style={{ WebkitTapHighlightColor: 'transparent' }}
        >
          <RotateCcw className="h-5 w-5" />
        </button>
      </div>

      {/* Framing hint — business card aspect ratio */}
      {!cameraError && (
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
          <div
            className="rounded-2xl border-2 border-white/70"
            style={{
              width: 'min(85vw, 400px)',
              height: 'min(48.5vw, 228px)',
              boxShadow: '0 0 0 9999px rgba(0,0,0,0.18)',
            }}
          />
        </div>
      )}
      {!cameraError && (
        <div
          className="absolute left-0 right-0 flex justify-center pointer-events-none"
          style={{ top: 'calc(env(safe-area-inset-top, 0px) + 64px)' }}
        >
          <span className="px-3 py-1 rounded-full bg-black/45 text-white text-[12px] font-medium tracking-tight">
            {isCameraStarting ? 'Starting camera…' : 'Position the card inside the frame'}
          </span>
        </div>
      )}

      {/* Bottom controls: photo library in the corner + shutter */}
      <div
        className="absolute bottom-0 left-0 right-0"
        style={{
          paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 20px)',
          background: 'linear-gradient(to top, rgba(0,0,0,0.75), rgba(0,0,0,0))',
        }}
      >
        <div className="flex items-center justify-between px-8 pt-10">
          {/* Photo library */}
          <button
            type="button"
            onClick={() => uploadInputRef.current?.click()}
            className="h-14 w-14 flex items-center justify-center rounded-2xl bg-white/15 text-white active:scale-[0.94] transition-transform"
            aria-label="Choose from photos"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <ImageIcon className="h-7 w-7" />
          </button>

          {/* Shutter */}
          <button
            type="button"
            onClick={handleSnapPhoto}
            disabled={!!cameraError || isCameraStarting}
            className="relative h-20 w-20 rounded-full flex items-center justify-center active:scale-[0.96] transition-transform disabled:opacity-50"
            aria-label="Take photo"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <span className="absolute inset-0 rounded-full border-[3px] border-white" />
            <span className="h-[60px] w-[60px] rounded-full bg-white" />
          </button>

          {/* Spacer to keep the shutter centered */}
          <div className="h-14 w-14" />
        </div>
      </div>
    </div>
  );

  const panelContent = (
    <div
      className="fixed inset-0 flex items-end sm:items-center justify-center"
      style={{
        zIndex: 10050,
        background: 'rgba(0, 0, 0, 0.5)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        paddingTop: 'env(safe-area-inset-top)',
        // --keyboard-inset lifts the whole panel above the iOS keyboard
        // (the webview itself never resizes — Keyboard.resize is 'none').
        paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + var(--keyboard-inset, 0px))',
      }}
      onClick={onClose}
    >
      <div
        className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl shadow-2xl flex flex-col"
        style={{
          maxHeight: isMobile
            ? 'calc(92vh - var(--keyboard-inset, 0px))'
            : '85vh',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-xl bg-blue-50 flex items-center justify-center">
              <ScanLine className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900" style={{ letterSpacing: '-0.02em' }}>
                Scan Business Card
              </h2>
              <p className="text-xs text-gray-500">AI creates the lead for you</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="h-9 w-9 flex items-center justify-center rounded-full hover:bg-gray-100 active:scale-95 transition-all"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4" onFocusCapture={handleFieldFocus}>
          {error && (
            <div className="mb-4 px-4 py-3 bg-red-50 border border-red-100 rounded-xl text-sm text-red-700">
              {error}
            </div>
          )}

          {step === 'capture' && (
            <div className="space-y-3">
              <p className="text-sm text-gray-600">
                Take a photo of a business card and the AI will read the contact
                details, match or create the customer, and open a new lead.
              </p>
              <button
                onClick={() => cameraInputRef.current?.click()}
                className="w-full flex items-center gap-3 px-4 py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-2xl font-semibold transition-all active:scale-[0.98]"
                style={{ boxShadow: '0 2px 8px rgba(37, 99, 235, 0.3)' }}
              >
                <Camera className="h-5 w-5" />
                Take Photo
              </button>
              <button
                onClick={() => uploadInputRef.current?.click()}
                className="w-full flex items-center gap-3 px-4 py-4 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-2xl font-semibold transition-all active:scale-[0.98]"
              >
                <Upload className="h-5 w-5" />
                Upload Image
              </button>
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => {
                  void handleFileSelected(e.target.files?.[0] ?? null);
                  e.target.value = '';
                }}
              />
              <input
                ref={uploadInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  void handleFileSelected(e.target.files?.[0] ?? null);
                  e.target.value = '';
                }}
              />
            </div>
          )}

          {step === 'processing' && (
            <div className="py-10 flex flex-col items-center text-center">
              {previewUrl && (
                <img
                  src={previewUrl}
                  alt="Business card"
                  className="max-h-40 rounded-xl shadow-md mb-5 object-contain"
                />
              )}
              <div className="flex items-center gap-2 text-blue-600 font-semibold">
                <Sparkles className="h-5 w-5 animate-pulse" />
                Reading the card…
              </div>
              <p className="text-sm text-gray-500 mt-1">Extracting name, phone, and email</p>
            </div>
          )}

          {(step === 'review' || step === 'creating') && (
            <div className="space-y-4">
              {previewUrl && (
                <img
                  src={previewUrl}
                  alt="Business card"
                  className="max-h-32 rounded-xl shadow-sm object-contain mx-auto"
                />
              )}

              {/* Match banner */}
              <div
                className={`flex items-start gap-2.5 px-4 py-3 rounded-xl border text-sm ${
                  matchedCustomer
                    ? 'bg-emerald-50 border-emerald-100 text-emerald-800'
                    : 'bg-blue-50 border-blue-100 text-blue-800'
                }`}
              >
                {matchedCustomer ? (
                  <UserCheck className="h-4 w-4 mt-0.5 flex-shrink-0" />
                ) : (
                  <UserPlus className="h-4 w-4 mt-0.5 flex-shrink-0" />
                )}
                <span>
                  {matchedCustomer ? (
                    <>
                      Matches existing customer{' '}
                      <strong>
                        {matchedCustomer.first_name} {matchedCustomer.last_name}
                      </strong>{' '}
                      — the lead will be linked to them.
                    </>
                  ) : (
                    'No matching customer found — a new customer will be created.'
                  )}
                </span>
              </div>

              {/* Section 1 — Customer info the AI picked up (editable) */}
              <div className="rounded-2xl border border-gray-200/80 overflow-hidden">
                <div className="flex items-center gap-2 px-4 py-2.5 bg-gray-50 border-b border-gray-100">
                  <UserPlus className="h-3.5 w-3.5 text-gray-400" />
                  <span className="text-[11px] font-bold uppercase tracking-wide text-gray-500">
                    Customer Info
                  </span>
                  <span className="ml-auto text-[10px] text-gray-400">
                    {matchedCustomer ? 'Linked to existing customer' : 'New customer'}
                  </span>
                </div>
                <div className="p-4 grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelClass}>First Name</label>
                    <input
                      value={fields.firstName}
                      onChange={(e) => setField('firstName', e.target.value)}
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Last Name</label>
                    <input
                      value={fields.lastName}
                      onChange={(e) => setField('lastName', e.target.value)}
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Phone</label>
                    <input
                      value={fields.phone}
                      onChange={(e) => setField('phone', e.target.value)}
                      className={inputClass}
                      inputMode="tel"
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Email</label>
                    <input
                      value={fields.email}
                      onChange={(e) => setField('email', e.target.value)}
                      className={inputClass}
                      inputMode="email"
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Company</label>
                    <input
                      value={fields.company}
                      onChange={(e) => setField('company', e.target.value)}
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Job Title</label>
                    <input
                      value={fields.jobTitle}
                      onChange={(e) => setField('jobTitle', e.target.value)}
                      className={inputClass}
                    />
                  </div>
                  <div className="col-span-2">
                    <label className={labelClass}>Website</label>
                    <input
                      value={fields.website}
                      onChange={(e) => setField('website', e.target.value)}
                      className={inputClass}
                    />
                  </div>
                  <div className="col-span-2">
                    <label className={labelClass}>Address</label>
                    <input
                      value={fields.address}
                      onChange={(e) => setField('address', e.target.value)}
                      className={inputClass}
                    />
                  </div>
                  <div className="col-span-2">
                    <label className={labelClass}>Notes</label>
                    <textarea
                      value={fields.notes}
                      onChange={(e) => setField('notes', e.target.value)}
                      className={`${inputClass} resize-none`}
                      rows={2}
                    />
                  </div>
                </div>
              </div>

              {/* Section 2 — How the lead will be entered in the CRM (editable) */}
              <div className="rounded-2xl border border-blue-200/70 overflow-hidden">
                <div className="flex items-center gap-2 px-4 py-2.5 bg-blue-50/70 border-b border-blue-100">
                  <Sparkles className="h-3.5 w-3.5 text-blue-500" />
                  <span className="text-[11px] font-bold uppercase tracking-wide text-blue-600">
                    Lead Details
                  </span>
                  <span className="ml-auto text-[10px] text-blue-400">
                    Entered into the CRM exactly as shown
                  </span>
                </div>
                <div className="p-4 space-y-3">
                  <div>
                    <label className={labelClass}>Lead Title</label>
                    <input
                      value={effectiveLeadTitle}
                      onChange={(e) => setLeadTitleOverride(e.target.value)}
                      className={inputClass}
                    />
                  </div>
                  {pipelines.length > 0 && (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className={labelClass}>Pipeline</label>
                        <select
                          value={selectedPipelineId ?? ''}
                          onChange={(e) => {
                            const id = Number(e.target.value);
                            setSelectedPipelineId(id);
                            const pipeline = pipelines.find((p) => p.id === id);
                            setSelectedStageId(pipeline?.stages[0]?.id ?? null);
                          }}
                          className={inputClass}
                        >
                          {pipelines.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.pipeline_name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className={labelClass}>Stage</label>
                        <select
                          value={selectedStageId ?? ''}
                          onChange={(e) => setSelectedStageId(Number(e.target.value))}
                          className={inputClass}
                        >
                          {(pipelines.find((p) => p.id === selectedPipelineId)?.stages ?? []).map(
                            (s) => (
                              <option key={s.id} value={s.id}>
                                {s.name}
                              </option>
                            ),
                          )}
                        </select>
                      </div>
                    </div>
                  )}
                  <div>
                    <label className={labelClass}>Source</label>
                    <input
                      value={leadSource}
                      onChange={(e) => setLeadSource(e.target.value)}
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Lead Info</label>
                    <textarea
                      value={effectiveLeadInfo}
                      onChange={(e) => setLeadInfoOverride(e.target.value)}
                      className={`${inputClass} resize-none`}
                      rows={4}
                    />
                    <p className="mt-1 text-[10px] text-gray-400">
                      Updates automatically from the customer info above until you edit it.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {(step === 'review' || step === 'creating') && (
          <div className="px-5 py-4 border-t border-gray-100 flex gap-3 flex-shrink-0">
            <button
              onClick={() => {
                setStep('capture');
                setError(null);
              }}
              disabled={step === 'creating'}
              className="px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-semibold text-sm transition-all active:scale-95 disabled:opacity-50"
            >
              Rescan
            </button>
            <button
              onClick={() => void handleCreateLead()}
              disabled={step === 'creating'}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold text-sm transition-all active:scale-[0.98] disabled:opacity-60"
              style={{ boxShadow: '0 2px 8px rgba(37, 99, 235, 0.3)' }}
            >
              {step === 'creating' ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Creating…
                </>
              ) : matchedCustomer ? (
                'Create Lead for Existing Customer'
              ) : (
                'Create Customer & Lead'
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(
    shouldShowLiveCamera ? liveCameraContent : panelContent,
    document.body,
  );
}
